import { authenticatedMiddleware } from '../../../permissions/middlewares/authenticatedMiddleware.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { registerSubscription } from '@adornis/baseql/metadata/register.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import type { Observable } from 'rxjs';
import { Booking } from '../../../bookings/entities/Booking.js';
import { Person } from '../../../users/entities/person.js';
import { Group } from '../../entities/Group.js';
import { resolveSubscribeGroupParticipantRowPaginatedFiltered } from '../server/resolvers/resolveSubscribeGroupParticipantRowPaginatedFiltered.js';

/**
 * One member of the groups list: a person with their bookings on the group, generated per active
 * booking (see `getRowBookings`). It also holds information to change the booking's values on the
 * fly from the group view.
 */
@Entity()
export class GroupParticipantRow extends AdornisEntity {
  static override _class = 'GroupParticipantRow';

  constructor(doc: PartialEntityData<GroupParticipantRow>) {
    super(doc);
  }

  static override resolveID(entity: GroupParticipantRow): string {
    return entity.booking._id;
  }

  /**
   * The booking the row speaks for - the active one, else the next upcoming, else the last. Edits of
   * the price and the link to the booking go to it.
   */
  @Field(() => Booking)
  booking!: Booking;

  /**
   * `booking` and the person's inactive bookings on the group, sorted by start: past and upcoming
   * school years that together make up the runtime, mandates and payments the row shows.
   */
  @Field(() => [Booking])
  bookings!: Booking[];

  /**
   * The Stichtag of the last lock export that collected anything of `bookings` - a booking's own
   * `billedOn` (Selbstzahler-Soll, one-time) or one of its mandates'. What we sent to the
   * Buchhaltung, before its Buchungsstapel comes back as accounting entries.
   */
  @Field(() => Date)
  exportedOn: Maybe<Date>;

  /**
   * The last collection of `booking` alone: its own `billedOn` or the latest of its mandates'. A
   * monthly or yearly Lastschrift marks only the mandate (see `Booking.billedOn`), so the booking's
   * own field misses those.
   */
  @Field(() => Date)
  billedOn: Maybe<Date>;

  /**
   * The user for the group
   */
  @Field(() => Person)
  user!: Person;

  /**
   * The group for which the row is generated. Used to show BillingPositions if not overriden
   */
  @Field(() => Group)
  group!: Group;
}

const subscribeGroupParticipantRowPaginatedFiltered = registerSubscription({
  type: () => [GroupParticipantRow],
  operationName: 'subscribeGroupParticipantRowPaginatedFiltered',
  middlewares: [authenticatedMiddleware],
  params: [
    { type: () => Int, name: 'skip' },
    { type: () => Int, name: 'limit' },
    { type: () => AdornisFilter, name: 'filter' },
  ],
  resolve:
    (skip: number, limit: number, filter: AdornisFilter) => (gqlFields: BaseQLSelectionSet<GroupParticipantRow>) =>
      resolveSubscribeGroupParticipantRowPaginatedFiltered(skip, limit, filter, gqlFields) as Observable<
        GroupParticipantRow[]
      >,
});

const subscribeGroupParticipantRowPaginatedFilteredCount = registerSubscription({
  type: () => Int,
  operationName: 'subscribeGroupParticipantRowPaginatedFilteredCount',
  middlewares: [authenticatedMiddleware],
  params: [{ type: () => AdornisFilter, name: 'filter' }],
  resolve: (filter: AdornisFilter) => () =>
    resolveSubscribeGroupParticipantRowPaginatedFiltered(0, -1, filter, {}, 'count') as Observable<number>,
});

/** The cents the bookings of the filtered rows pay - every row, not one page. */
const subscribeGroupParticipantRowPaginatedFilteredPriceSum = registerSubscription({
  type: () => Int,
  operationName: 'subscribeGroupParticipantRowPaginatedFilteredPriceSum',
  middlewares: [authenticatedMiddleware],
  params: [{ type: () => AdornisFilter, name: 'filter' }],
  resolve: (filter: AdornisFilter) => () =>
    resolveSubscribeGroupParticipantRowPaginatedFiltered(0, -1, filter, {}, 'sum') as Observable<number>,
});

/**
 * The answers of the members list's "SEPA-Mandat" filter, carried as the value of the `sepaStatus`
 * StringAspect. The three are disjoint: a member with a 0 anywhere - on a running mandate
 * (gefördert, befreit, pausiert) or on the booking's effective price - is neither an accepted nor a
 * missing SEPA, so the SEPA counts stay in step with the lines of the export.
 */
export enum GroupSepaFilter {
  /** A running mandate an export collects money from. */
  ACCEPTED = 'accepted',
  /** No such mandate: a Selbstzahler the export gives a Soll. */
  NOT_ACCEPTED = 'notAccepted',
  /** Nothing to collect: a running mandate over 0, or a booking whose effective price is 0. */
  ZERO_AMOUNT = 'zeroAmount',
}

const getFilterAspects = (params: { groupID: Maybe<string> }) => {
  return [params.groupID ? new StringAspect({ customID: 'groupID', value: params.groupID }) : undefined].filter(
    aspect => aspect !== undefined,
  );
};

export const GroupsDetailsAPI = {
  subscribeMembers: subscribeGroupParticipantRowPaginatedFiltered,
  subscribeMembersCount: subscribeGroupParticipantRowPaginatedFilteredCount,
  subscribeMembersPriceSum: subscribeGroupParticipantRowPaginatedFilteredPriceSum,
  getFilterAspects,
};
